import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useContext,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import InputNumberFormat from 'react-number-format';

import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowRight as ArrowRightIcon } from '@styled-icons/bootstrap/ArrowRight';
import { InfoCircleFill as InfoCircleFillIcon } from '@styled-icons/bootstrap/InfoCircleFill';
import { Refresh as RefreshIcon } from '@styled-icons/material-twotone/Refresh';
import TableRemoveIcon from 'mdi-react/TableRemoveIcon';
import TableSearchIcon from 'mdi-react/TableSearchIcon';
import { api } from 'services/api';
import { toast } from 'shared/toast';
import * as yup from 'yup';

import {
  Button,
  Icon,
  Flex,
  Grid,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Box,
  Text,
  Input,
  Textarea,
  GridItem,
  InputGroup,
  InputRightAddon,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';

import Loader from 'components/Loader';
import OwnSelect from 'components/OwnSelect';
import { SeparatedContent } from 'components/PageContainer/styles';
import SiengeBadge from 'components/SiengeBadge';

import AuthContext from 'contexts/AuthContext';

import useModal from 'hooks/useModal';

import {
  Classification,
  ClassificationType,
  UnitMeasure,
  Base,
} from 'types/base';
import { Budget } from 'types/budget';
import { CompositionGroup } from 'types/composition';
import { IntegrationUnitMeasure } from 'types/unit-measure';

export type CompositionIdentification = {
  base_id?: number;
  base_model_type?: string;
  version_id?: number;
  classification_id?: number;
  type_id?: number;
  unit_measure_id?: number;
  external_unit_measure_key?: string | null;
  external_classification_key?: string | null;
  external_type_key?: string | null;
  external_classification_description?: string | null;
  external_type_description?: string | null;
  code: string;
  description: string;
  observation: string;
  kind: 'common' | 'informed';
  quantity?: number;
};

interface FormProps {
  onSubmit(data: CompositionIdentification): void;
  initialData?: CompositionIdentification;
  saving?: boolean;
  loading: boolean;
  isEditing: boolean;
  isFromModal?: boolean;
  budgetBaseOptions?: Budget['bases'];
  isKindLocked?: boolean;
  fixedKind?: CompositionIdentification['kind'];
  hideKindField?: boolean;
  disableBaseVersionWhenInitialData?: boolean;
  showQuantityField?: boolean;
}

type Option = {
  value: string | number;
  label: string;
  type?: string;
  disabled?: boolean;
};

type CompositionGroupSelection = {
  parent?: Partial<CompositionGroup>;
  selected?: Partial<CompositionGroup>;
};

type BudgetBaseOption = Budget['bases'][number] & {
  version: Budget['bases'][number]['version'] & {
    base: Budget['bases'][number]['version']['base'] & {
      model_type?: string;
    };
  };
};

type BaseSelection = {
  id: number;
  description: string;
  model_type?: string;
};

type VersionSelection = {
  id: number;
  description: string;
};

const formatOptionLabel = ({ label, type, disabled }: Option): JSX.Element => {
  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="flex-start"
      gridGap={2}
    >
      <Text>{label}</Text>

      {type === 'sienge' && (
        <Box opacity={disabled ? 0.5 : 1}>
          <SiengeBadge />
        </Box>
      )}
    </Box>
  );
};

const siengeTooltipMessages = {
  unitMeasure: `As unidades de medida exibidas nesta lista são provenientes do Sienge Plataforma. Caso seja necessário cadastrar uma nova unidade, realize o cadastro diretamente no Sienge. Após isso, ela ficará disponível para seleção no eCustos.`,
  serviceGroup: `Os grupos de serviço exibidos nesta lista são provenientes do Sienge Plataforma. Caso seja necessário cadastrar um novo grupo, realize o cadastro diretamente no Sienge. Após isso, ele ficará disponível para seleção no eCustos.`,
};

const SiengeFieldLabelWithTooltip: React.FC<{
  label: string;
  tooltipLabel: string;
}> = ({ label, tooltipLabel }) => {
  return (
    <FormLabel display="flex" alignItems="center" gridGap={1}>
      {label}
      <Tooltip hasArrow label={tooltipLabel} placement="top-start" maxW="380px">
        <IconButton
          type="button"
          aria-label={`Informações sobre ${label.toLowerCase()}`}
          title={`Informações sobre ${label.toLowerCase()}`}
          variant="ghost"
          colorScheme="gray"
          size="xs"
          minWidth="auto"
          height="18px"
          width="18px"
          icon={<Icon as={InfoCircleFillIcon} w="14px" h="14px" />}
        />
      </Tooltip>
    </FormLabel>
  );
};

const FormCompositionIdentification: React.FC<FormProps> = ({
  initialData,
  onSubmit,
  saving,
  loading,
  isEditing,
  isFromModal = false,
  budgetBaseOptions,
  isKindLocked = false,
  fixedKind,
  hideKindField = false,
  disableBaseVersionWhenInitialData = true,
  showQuantityField = false,
}) => {
  const { openModal } = useModal();
  const FormContentWrapper = (
    isFromModal ? Box : SeparatedContent
  ) as React.ElementType;
  const resolvedFixedKind =
    fixedKind || (isKindLocked ? 'informed' : undefined);
  const validationSchema = yup.object().shape({
    base_id: yup.string().required(),
    version_id: yup.string().required(),
    classification_id: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null ? undefined : value,
      )
      .nullable()
      .when('base_id', {
        is: (base_id: string) => {
          const base = baseList.find((b) => b.id === Number(base_id));
          return base?.model_type !== 'sienge';
        },
        then: yup.number().required('Classificação é obrigatória.'),
        otherwise: yup.number().nullable(),
      }),
    type_id: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null ? undefined : value,
      )
      .nullable()
      .when('base_id', {
        is: (base_id: string) => {
          const base = baseList.find((b) => b.id === Number(base_id));
          return base?.model_type !== 'sienge';
        },
        then: yup.number().required('Subgrupo de serviço é obrigatório.'),
        otherwise: yup.number().nullable(),
      }),
    external_classification_key: yup.string().when('base_id', {
      is: (base_id: string) => {
        const base = baseList.find((b) => b.id === Number(base_id));
        return base?.model_type === 'sienge';
      },
      then: yup.string().required('Grupo de serviço é obrigatório.'),
      otherwise: yup.string().nullable(),
    }),
    external_type_key: yup.string().when('base_id', {
      is: (base_id: string) => {
        const base = baseList.find((b) => b.id === Number(base_id));
        return base?.model_type === 'sienge';
      },
      then: yup.string().required('Grupo de serviço é obrigatório.'),
      otherwise: yup.string().nullable(),
    }),
    unit_measure_id: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null ? undefined : value,
      )
      .nullable()
      .test(
        'unit-measure-required',
        'Unidade de medida é obrigatória.',
        function testUnitMeasure(value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const baseId = this.parent.base_id;
          const base = baseList.find((b) => b.id === Number(baseId));
          const isSiengeBase = base?.model_type === 'sienge';

          if (isSiengeBase) {
            // eslint-disable-next-line react/no-this-in-sfc
            return !!this.parent.external_unit_measure_key;
          }

          return value !== undefined && value !== null;
        },
      ),
    external_unit_measure_key: yup.string().nullable(),
    description: yup.string().required('Descrição é obrigatória.'),
    code: yup.string().required('Código é obrigatório.'),
    observation: yup.string().nullable(),
    kind: yup
      .string()
      .oneOf(['common', 'informed'])
      .required('Tipo do preço é obrigatório.'),
    quantity: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null ? undefined : value,
      )
      .nullable()
      .test(
        'quantity-required',
        'Quantidade deve ser maior que zero.',
        (value) => {
          if (!showQuantityField) {
            return true;
          }

          return Number(value) > 0;
        },
      ),
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    control,
    clearErrors,
  } = useForm<CompositionIdentification>({
    defaultValues: initialData,
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    register('external_unit_measure_key');
    register('external_classification_key');
    register('external_type_key');
    register('external_classification_description');
    register('external_type_description');
    register('quantity');
  }, [register]);

  const { user } = useContext(AuthContext);

  const [baseSelected, setBaseSelected] = useState(
    initialData?.base_id ? Number(initialData?.base_id) : '',
  );
  const [baseList, setBaseList] = useState<BaseSelection[]>([]);
  const [isFindingBases, setIsFindingBases] = useState(false);
  const shouldUseBudgetBaseOptions =
    isFromModal &&
    Array.isArray(budgetBaseOptions) &&
    budgetBaseOptions.length > 0;

  const budgetBasesByBaseId = useMemo(() => {
    if (!budgetBaseOptions?.length) {
      return {} as Record<number, BudgetBaseOption[]>;
    }

    return budgetBaseOptions.reduce<Record<number, BudgetBaseOption[]>>(
      (acc, rawBudgetBase) => {
        const budgetBase = rawBudgetBase as BudgetBaseOption;
        const modelType = budgetBase?.version?.base?.model_type;
        const baseId = budgetBase.version.base.id;

        if (!modelType || modelType !== 'sienge') {
          return acc;
        }

        if (!acc[baseId]) {
          acc[baseId] = [];
        }

        acc[baseId].push(budgetBase);
        return acc;
      },
      {},
    );
  }, [budgetBaseOptions]);

  const selectedBase = useMemo(() => {
    if (!baseSelected) {
      return undefined;
    }

    const selectedId = Number(baseSelected);
    return baseList.find((base) => base.id === selectedId);
  }, [baseList, baseSelected]);

  const selectedBaseModelType = selectedBase?.model_type;
  const activeBaseModelType = useMemo(() => {
    if (selectedBaseModelType) {
      return selectedBaseModelType;
    }

    return initialData?.base_model_type;
  }, [initialData?.base_model_type, selectedBaseModelType]);

  const isSiengeBase = activeBaseModelType === 'sienge';

  const [versionSelected, setVersionSelected] = useState(
    initialData?.version_id ? Number(initialData?.version_id) : '',
  );
  const [versionList, setVersionList] = useState<VersionSelection[]>([]);
  const [isFindingVersions, setIsFindingVersions] = useState(false);

  const [classificationSelected, setClassificationSelected] = useState(
    initialData?.classification_id
      ? Number(initialData?.classification_id)
      : '',
  );

  const [classificationTypeSelected, setClassificationTypeSelected] = useState(
    initialData?.type_id ? Number(initialData?.type_id) : '',
  );

  const [classificationList, setClassificationList] = useState<
    Classification[]
  >([]);
  const [classificationTypeList, setClassificationTypeList] = useState<
    ClassificationType[]
  >([]);
  const [isFindingClassifications, setIsFindingClassifications] =
    useState(false);
  const [isFindingClassificationTypes, setIsFindingClassificationTypes] =
    useState(false);

  const [compositionGroup, setCompositionGroup] =
    useState<CompositionGroupSelection>({});

  const isCompositionGroupSelected = !!(
    compositionGroup.parent && compositionGroup.selected
  );

  const compositionGroupLabel = useMemo(() => {
    if (!compositionGroup.parent || !compositionGroup.selected) {
      return '';
    }

    const formatGroup = (group?: Partial<CompositionGroup>): string => {
      if (!group) {
        return '';
      }

      return [group.key, group.description].filter(Boolean).join(' - ');
    };

    // const parentLabel = formatGroup(compositionGroup.parent);
    const selectedLabel = formatGroup(compositionGroup.selected);

    return selectedLabel;
  }, [compositionGroup]);

  const [unitMeasureSelected, setUnitMeasureSelected] = useState<string>(() => {
    if (initialData?.external_unit_measure_key) {
      return initialData.external_unit_measure_key;
    }

    if (initialData?.unit_measure_id) {
      return String(initialData.unit_measure_id);
    }

    return '';
  });

  const [quantityValue, setQuantityValue] = useState<number | ''>(() => {
    if (initialData?.quantity !== undefined && initialData?.quantity !== null) {
      return Number(initialData.quantity);
    }

    return '';
  });
  const [unitMeasureList, setUnitMeasureList] = useState<UnitMeasure[]>([]);
  const [integrationUnitMeasureList, setIntegrationUnitMeasureList] = useState<
    IntegrationUnitMeasure[]
  >([]);
  const [isFindingUnitMeasures, setIsFindingUnitMeasures] = useState(false);

  const [isFindingCode, setIsFindingCode] = useState(false);

  useEffect(() => {
    setValue('base_model_type', activeBaseModelType);
  }, [activeBaseModelType, setValue]);

  useEffect(() => {
    if (isSiengeBase) {
      setClassificationSelected('');
      setClassificationTypeSelected('');
      setValue('classification_id', undefined);
      setValue('type_id', undefined);
      clearErrors(['classification_id', 'type_id']);
    } else {
      setCompositionGroup({});
      setValue('external_classification_key', undefined);
      setValue('external_type_key', undefined);
      clearErrors(['external_classification_key', 'external_type_key']);
    }
  }, [clearErrors, isSiengeBase, setValue]);

  const clearUnitMeasureSelection = useCallback(() => {
    setUnitMeasureSelected('');
    setValue('unit_measure_id', undefined);
    setValue('external_unit_measure_key', undefined);
  }, [setValue]);

  const [priceKindSelected, setPriceKindSelected] = useState<
    CompositionIdentification['kind']
  >(resolvedFixedKind || initialData?.kind || 'common');

  const priceKindOptions = useMemo(() => {
    if (resolvedFixedKind) {
      return [
        {
          value: resolvedFixedKind,
          label: resolvedFixedKind === 'informed' ? 'Informado' : 'Composto',
        },
      ];
    }

    const options = [{ value: 'common', label: 'Composto' }];

    if (isSiengeBase) {
      options.push({ value: 'informed', label: 'Informado' });
    }

    return options;
  }, [isSiengeBase, resolvedFixedKind]);

  const displayedPriceKind = isSiengeBase ? priceKindSelected : 'common';
  const shouldUseIntegrationUnitMeasures = isSiengeBase;
  const compositionGroupError =
    errors.external_classification_key || errors.external_type_key;

  useEffect(() => {
    if (resolvedFixedKind) {
      setPriceKindSelected(resolvedFixedKind);
      setValue('kind', resolvedFixedKind);
      return;
    }

    if (!isSiengeBase && priceKindSelected !== 'common') {
      setPriceKindSelected('common');
      setValue('kind', 'common');
    }
  }, [isSiengeBase, priceKindSelected, resolvedFixedKind, setValue]);

  const getClassifications = useCallback(async (baseId) => {
    try {
      setIsFindingClassifications(true);
      const { data: response } = await api.get(
        'composition/classification/template',
        {
          params: {
            'filter[base_id]': baseId,
          },
        },
      );
      const classificationsLoaded = response.data;
      setClassificationList(classificationsLoaded);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao carregar os grupos de serviço dessa base!',
        status: 'error',
      });
    } finally {
      setIsFindingClassifications(false);
    }
  }, []);

  const getClassificationTypes = useCallback(async (classificationId) => {
    try {
      setIsFindingClassificationTypes(true);
      const { data: response } = await api.get('/composition/type/template', {
        params: {
          'filter[classification_id]': classificationId,
        },
      });
      const classificationsLoaded = response.data;

      if (classificationsLoaded.length === 0) {
        toast({
          description: `Não há subgrupos de serviço cadastrados para esse grupo!`,
          status: 'error',
        });
      }

      setClassificationTypeList(classificationsLoaded);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao carregar os subgrupos de serviço dessa base!',
        status: 'error',
      });
    } finally {
      setIsFindingClassificationTypes(false);
    }
  }, []);

  const handleChangeBase = useCallback(
    async (baseId: number) => {
      setIsFindingVersions(true);
      setCompositionGroup({});
      setValue('external_classification_key', undefined);
      setValue('external_type_key', undefined);
      setClassificationSelected('');
      setClassificationTypeSelected('');
      setValue('classification_id', undefined);
      setValue('type_id', undefined);

      if (shouldUseBudgetBaseOptions) {
        const budgetBasesForSelectedBase = budgetBasesByBaseId[baseId] || [];
        const versions: VersionSelection[] = budgetBasesForSelectedBase.map(
          (budgetBase) => ({
            id: budgetBase.version.id,
            description: budgetBase.version.description,
          }),
        );

        setVersionList(versions);

        if (versions.length === 1) {
          const [version] = versions;
          setVersionSelected(version.id);
          setValue('version_id', version.id);
        } else {
          setVersionSelected('');
          setValue('version_id', undefined);
        }

        setIsFindingVersions(false);
        return;
      }

      try {
        const response = await api.get('/version/list', {
          params: {
            'filter[organization_id]':
              user.managed_organization.organization.id,
            'filter[base_id]': baseId,
          },
        });

        const versions = response.data;
        setVersionList(versions);

        if (versions.length === 1) {
          const [version] = versions;
          setVersionSelected(version.id);
          setValue('version_id', version.id);
        }
      } catch (err) {
        toast({
          description:
            err.response?.data?.message ||
            'Houve um erro ao tentar carregar a lista de referências.',
          status: 'error',
        });
      } finally {
        setIsFindingVersions(false);
      }

      getClassifications(baseId);
    },
    [
      shouldUseBudgetBaseOptions,
      budgetBasesByBaseId,
      user,
      getClassifications,
      setValue,
      setClassificationSelected,
      setClassificationTypeSelected,
      setCompositionGroup,
    ],
  );

  const getBases = useCallback(async () => {
    if (shouldUseBudgetBaseOptions) {
      const uniqueBases = Object.values(budgetBasesByBaseId)
        .map((budgetBases) => budgetBases[0]?.version?.base)
        .filter(Boolean)
        .map((base) => ({
          id: base?.id as number,
          description: base?.description as string,
          model_type: base?.model_type,
        }));

      setBaseList(uniqueBases);

      if (uniqueBases.length === 1) {
        const [base] = uniqueBases;
        setBaseSelected(base.id);
        setValue('base_id', base.id);
        handleChangeBase(base.id);
      }

      return;
    }

    setIsFindingBases(true);

    try {
      const { data: basesLoaded } = await api.get('/base/list', {
        params: {
          'filter[organization_id]': user.managed_organization.organization.id,
          sort: 'description',
        },
      });

      setBaseList(
        (basesLoaded || []).map((base: Base) => ({
          id: base.id,
          description: base.description,
          model_type: base.model_type,
        })),
      );

      if ((basesLoaded || []).length === 1) {
        const [base] = basesLoaded;
        setBaseSelected(base.id);
        setValue('base_id', base.id);
        handleChangeBase(base.id);
      }
    } catch (err) {
      toast({
        description:
          err.response?.data?.message || 'Houve um erro ao carregar as bases!',
        status: 'error',
      });
    } finally {
      setIsFindingBases(false);
    }
  }, [
    shouldUseBudgetBaseOptions,
    budgetBasesByBaseId,
    user,
    handleChangeBase,
    setValue,
  ]);

  const basesToSelect = useMemo(
    () =>
      baseList.map((base) => ({
        value: base.id,
        label: base.description,
        type: base.model_type,
      })),
    [baseList],
  );

  const versionsToSelect = useMemo(
    () =>
      versionList.map((version) => ({
        value: version.id,
        label: version.description,
      })),
    [versionList],
  );

  const classificationsToSelect = useMemo(
    () =>
      classificationList.map((classification) => ({
        value: classification.id,
        label: classification.description,
      })),
    [classificationList],
  );

  const classificationTypesToSelect = useMemo(() => {
    return classificationTypeList.map((type) => ({
      value: String(type.id),
      label: type.description,
    }));
  }, [classificationTypeList]);

  useEffect(() => {
    getClassificationTypes(classificationSelected);
  }, [getClassificationTypes, classificationSelected]);

  const getUnitMeasures = useCallback(async () => {
    setIsFindingUnitMeasures(true);

    try {
      const response = await api.get('/unit/measure/list', {
        params: {
          'filter[no_organization]': '.',
        },
      });
      const unitMeasuresLoaded = response.data;
      setUnitMeasureList(unitMeasuresLoaded.data);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao carregar as unidades de medida!',
        status: 'error',
      });
    } finally {
      setIsFindingUnitMeasures(false);
    }
  }, []);

  const unitMeasuresToSelect = useMemo(() => {
    if (shouldUseIntegrationUnitMeasures) {
      return integrationUnitMeasureList.map((unitMeasure) => ({
        value: unitMeasure.key,
        label: `${unitMeasure.description} (${unitMeasure.key})`,
      }));
    }

    return unitMeasureList.map((unitMeasure) => ({
      value: String(unitMeasure.id),
      label: unitMeasure.description,
    }));
  }, [
    integrationUnitMeasureList,
    shouldUseIntegrationUnitMeasures,
    unitMeasureList,
  ]);

  useEffect(() => {
    if (!saving) {
      getBases();
      getUnitMeasures();
    }
  }, [getBases, getUnitMeasures, saving]);

  useEffect(() => {
    if (!unitMeasureSelected) {
      return;
    }

    if (isFindingUnitMeasures || unitMeasuresToSelect.length === 0) {
      return;
    }

    const normalize = (value: string | number | null | undefined): string => {
      if (value === undefined || value === null) {
        return '';
      }

      const stringValue = String(value);

      return stringValue.toLowerCase();
    };

    const normalizedSelected = normalize(unitMeasureSelected);

    const matchedOption = unitMeasuresToSelect.find(
      (option) => normalize(option.value) === normalizedSelected,
    );

    if (matchedOption) {
      const matchedValueAsString =
        matchedOption.value === undefined || matchedOption.value === null
          ? ''
          : String(matchedOption.value);

      if (matchedValueAsString !== unitMeasureSelected) {
        setUnitMeasureSelected(matchedValueAsString);
        return;
      }
    }

    if (!matchedOption) {
      const isEditingInitialBase =
        isEditing &&
        initialData?.base_id &&
        Number(baseSelected || initialData.base_id) ===
          Number(initialData.base_id);

      if (isEditingInitialBase) {
        return;
      }

      clearUnitMeasureSelection();
    }
  }, [
    baseSelected,
    clearUnitMeasureSelection,
    initialData?.base_id,
    isEditing,
    isFindingUnitMeasures,
    unitMeasureSelected,
    unitMeasuresToSelect,
    shouldUseIntegrationUnitMeasures,
  ]);

  const getIntegrationUnitMeasures = useCallback(async () => {
    setIsFindingUnitMeasures(true);

    try {
      const response = await api.get('integration/unit/measure', {
        params: {
          page: 1,
          per_page: 9999,
        },
      });
      const unitMeasuresLoaded = response.data;
      const data: IntegrationUnitMeasure[] =
        unitMeasuresLoaded?.data || unitMeasuresLoaded || [];
      setIntegrationUnitMeasureList(data);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao carregar as unidades de medida!',
        status: 'error',
      });
    } finally {
      setIsFindingUnitMeasures(false);
    }
  }, []);

  useEffect(() => {
    if (
      shouldUseIntegrationUnitMeasures &&
      integrationUnitMeasureList.length === 0
    ) {
      getIntegrationUnitMeasures();
    }
  }, [
    getIntegrationUnitMeasures,
    integrationUnitMeasureList.length,
    shouldUseIntegrationUnitMeasures,
  ]);

  useEffect(() => {
    if (!selectedBase) {
      return;
    }

    if (shouldUseIntegrationUnitMeasures) {
      setValue('unit_measure_id', undefined);
    } else {
      setValue('external_unit_measure_key', undefined);
    }
  }, [selectedBase, setValue, shouldUseIntegrationUnitMeasures]);

  const getNextCode = useCallback(
    async (versionId) => {
      setIsFindingCode(true);

      try {
        const { data: responseCode } = await api.get('/composition/code', {
          params: {
            'filter[version_id]': versionId,
            'filter[show_with_trashed]': '.',
          },
        });

        setValue('code', responseCode);
      } catch (err) {
        toast({
          description:
            err.response?.data?.message ||
            'Houve um erro ao tentar carregar o código do serviço. Preencha o campo manualmente',
          status: 'error',
        });
      } finally {
        setIsFindingCode(false);
      }
    },
    [setValue],
  );

  useEffect(() => {
    if (versionSelected && !initialData?.code) {
      getNextCode(versionSelected);
    }
  }, [versionSelected, getNextCode, initialData?.code]);

  useEffect(() => {
    if (initialData?.base_id && !saving) {
      setBaseSelected(initialData.base_id);
      setValue('base_id', initialData.base_id);
      handleChangeBase(initialData.base_id);
    }

    /* eslint-disable-next-line */
  }, [initialData?.base_id, saving]);

  const handleOnSubmit = useCallback(
    (data: CompositionIdentification) => {
      const normalizedKind =
        resolvedFixedKind || (isSiengeBase ? data.kind : 'common');

      const payload: CompositionIdentification = {
        ...data,
        kind: normalizedKind,
        base_model_type: activeBaseModelType,
      };

      if (isSiengeBase) {
        payload.classification_id = undefined;
        payload.type_id = undefined;
        delete payload.external_classification_description;
        delete payload.external_type_description;
      } else {
        payload.external_classification_key = undefined;
        payload.external_type_key = undefined;
        payload.external_classification_description = undefined;
        payload.external_type_description = undefined;
      }

      onSubmit(payload);
    },
    [activeBaseModelType, isSiengeBase, onSubmit, resolvedFixedKind],
  );

  useEffect(() => {
    if (initialData?.code) {
      setValue('code', initialData?.code);
    }

    if (initialData?.description) {
      setValue('description', initialData?.description);
    }

    if (initialData?.base_id) {
      setBaseSelected(Number(initialData?.base_id));
      setValue('base_id', initialData?.base_id);
    }

    if (initialData?.version_id) {
      setVersionSelected(Number(initialData?.version_id));
      setValue('version_id', initialData?.version_id);
    }

    if (initialData?.external_unit_measure_key) {
      setUnitMeasureSelected(initialData.external_unit_measure_key);
      setValue(
        'external_unit_measure_key',
        initialData.external_unit_measure_key,
      );
      setValue('unit_measure_id', undefined);
    } else if (initialData?.unit_measure_id) {
      setUnitMeasureSelected(String(initialData.unit_measure_id));
      setValue('unit_measure_id', initialData.unit_measure_id);
    }

    if (initialData?.classification_id) {
      setClassificationSelected(Number(initialData?.classification_id));
      setValue('classification_id', initialData?.classification_id);
    }

    if (initialData?.type_id) {
      setClassificationTypeSelected(Number(initialData?.type_id));
      setValue('type_id', initialData?.type_id);
    }

    if (
      initialData?.external_classification_key ||
      initialData?.external_type_key
    ) {
      setCompositionGroup({
        parent: initialData.external_classification_key
          ? {
              key: initialData.external_classification_key,
              description:
                initialData.external_classification_description || '',
            }
          : undefined,
        selected: initialData.external_type_key
          ? {
              key: initialData.external_type_key,
              description: initialData.external_type_description || '',
            }
          : undefined,
      });
      if (initialData.external_classification_key) {
        setValue(
          'external_classification_key',
          initialData.external_classification_key,
        );
        setValue(
          'external_classification_description',
          initialData.external_classification_description || undefined,
        );
      }
      if (initialData.external_type_key) {
        setValue('external_type_key', initialData.external_type_key);
        setValue(
          'external_type_description',
          initialData.external_type_description || undefined,
        );
      }
    }

    if (initialData?.observation) {
      setValue('observation', initialData?.observation);
    }

    if (resolvedFixedKind) {
      setPriceKindSelected(resolvedFixedKind);
      setValue('kind', resolvedFixedKind);
    } else if (initialData?.kind) {
      setPriceKindSelected(initialData.kind);
      setValue('kind', initialData.kind);
    } else {
      setPriceKindSelected('common');
      setValue('kind', 'common');
    }

    if (initialData?.quantity !== undefined) {
      const nextQuantity = Number(initialData.quantity);
      setQuantityValue(nextQuantity);
      setValue('quantity', nextQuantity);
    } else {
      setQuantityValue('');
      setValue('quantity', undefined);
    }
  }, [initialData, resolvedFixedKind, setValue]);

  return (
    <Flex
      w="100%"
      as="form"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      position="relative"
      sx={{
        '> *': {
          marginY: 1,
        },
      }}
      onSubmit={handleSubmit(handleOnSubmit)}
    >
      {(loading || saving) && (
        <Flex
          position="absolute"
          top={-1}
          left={-1}
          right={-1}
          bottom={-1}
          zIndex={99}
          bg="white"
          opacity={0.7}
        >
          <Loader />
        </Flex>
      )}
      <FormContentWrapper w="100%">
        <Grid
          templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
          columnGap={2}
          rowGap={2}
          width="100%"
        >
          <FormControl isInvalid={!!errors.base_id}>
            <FormLabel>Base de dados</FormLabel>

            <Controller
              render={(field) => (
                <OwnSelect
                  {...field}
                  isInvalid={!!errors.base_id}
                  placeholder="Selecione"
                  options={basesToSelect}
                  value={basesToSelect.filter((s) => s.value === baseSelected)}
                  isLoading={isFindingBases}
                  isDisabled={
                    isFindingBases ||
                    basesToSelect.length === 0 ||
                    (disableBaseVersionWhenInitialData &&
                      !!initialData?.base_id)
                  }
                  onChange={(selected) => {
                    setBaseSelected(Number(selected?.value));
                    setValue('base_id', Number(selected?.value));
                    handleChangeBase(Number(selected?.value));
                    clearUnitMeasureSelection();
                    setCompositionGroup({});
                    setValue('external_classification_key', undefined);
                    setValue('external_type_key', undefined);
                    setClassificationSelected('');
                    setClassificationTypeSelected('');
                    setValue('classification_id', undefined);
                    setValue('type_id', undefined);
                    clearErrors([
                      'base_id',
                      'external_classification_key',
                      'external_type_key',
                      'classification_id',
                      'type_id',
                    ]);

                    if (selected && !!errors.base_id) {
                      clearErrors('base_id');
                    }
                  }}
                  formatOptionLabel={(props) => {
                    return formatOptionLabel({
                      disabled:
                        isFindingBases ||
                        basesToSelect.length === 0 ||
                        (disableBaseVersionWhenInitialData &&
                          !!initialData?.base_id),
                      ...props,
                    });
                  }}
                />
              )}
              name="base_id"
              control={control}
            />

            <FormErrorMessage>
              {errors.base_id && 'Base é obrigatória.'}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.version_id}>
            <FormLabel>Referência</FormLabel>

            <Controller
              render={(field) => (
                <OwnSelect
                  {...field}
                  isInvalid={!!errors.version_id}
                  placeholder="Selecione"
                  options={versionsToSelect}
                  value={versionsToSelect.filter(
                    (s) => s.value === versionSelected,
                  )}
                  isLoading={isFindingVersions}
                  isDisabled={
                    isFindingVersions ||
                    versionsToSelect.length === 0 ||
                    (disableBaseVersionWhenInitialData &&
                      !!initialData?.version_id)
                  }
                  onChange={(selected) => {
                    setVersionSelected(Number(selected?.value));
                    setValue('version_id', Number(selected?.value));

                    if (selected && !!errors.version_id) {
                      clearErrors('version_id');
                    }
                  }}
                />
              )}
              name="version_id"
              control={control}
              rules={{ required: true }}
            />

            <FormErrorMessage>
              {errors.version_id && 'Referência é obrigatória.'}
            </FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid
          gridTemplateColumns={{
            base: '1fr',
            lg: isSiengeBase ? '250px 1fr 250px' : '250px 1fr',
          }}
          columnGap={{ base: 0, md: 2 }}
          rowGap={2}
          marginTop={4}
          width="100%"
        >
          <GridItem>
            <FormControl isInvalid={!!errors.code}>
              <FormLabel>Código</FormLabel>

              <InputGroup>
                <Input
                  type="text"
                  placeholder="Código do serviço"
                  defaultValue={initialData?.code}
                  disabled={isFindingCode}
                  {...register('code')}
                />

                <InputRightAddon px={0}>
                  <IconButton
                    height="100%"
                    colorScheme="blue"
                    aria-label={
                      versionSelected
                        ? 'Gerar código válido'
                        : 'Selecione uma versão'
                    }
                    title={
                      versionSelected
                        ? 'Gerar código válido'
                        : 'Selecione uma versão'
                    }
                    borderTopLeftRadius="0"
                    borderBottomLeftRadius="0"
                    onClick={() => getNextCode(versionSelected)}
                    onBlur={() => getNextCode(versionSelected)}
                    isLoading={isFindingCode}
                    isDisabled={isFindingCode || !versionSelected}
                    width="50px"
                  >
                    <Icon as={RefreshIcon} w="18px" h="18px" />
                  </IconButton>
                </InputRightAddon>
              </InputGroup>

              <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl isInvalid={!!errors.description}>
              <FormLabel>Descrição</FormLabel>

              <Input
                type="text"
                placeholder="Descrição do serviço"
                defaultValue={initialData?.description}
                {...register('description')}
              />

              <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          {isSiengeBase && !hideKindField && (
            <GridItem>
              <FormControl isInvalid={!!errors.kind}>
                <FormLabel>Tipo do preço</FormLabel>

                <Controller
                  render={(field) => (
                    <OwnSelect
                      {...field}
                      data-testid="kind-select"
                      isInvalid={!!errors.kind}
                      placeholder="Selecione"
                      options={priceKindOptions}
                      value={priceKindOptions.filter(
                        (option) => option.value === displayedPriceKind,
                      )}
                      onChange={(selected) => {
                        const value = (selected?.value ||
                          'common') as CompositionIdentification['kind'];
                        setPriceKindSelected(value);
                        setValue('kind', value);

                        if (selected && !!errors.kind) {
                          clearErrors('kind');
                        }
                      }}
                      isDisabled={isEditing || isKindLocked}
                    />
                  )}
                  name="kind"
                  control={control}
                  rules={{ required: true }}
                />

                <FormErrorMessage>{errors.kind?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
          )}

          {showQuantityField && (
            <GridItem>
              <FormControl isInvalid={!!errors.quantity}>
                <FormLabel>Quantidade</FormLabel>
                <InputNumberFormat
                  type="tel"
                  className="text-right"
                  displayType="input"
                  thousandSeparator="."
                  decimalSeparator=","
                  allowNegative={false}
                  decimalScale={7}
                  fixedDecimalScale={false}
                  value={quantityValue}
                  placeholder="1"
                  customInput={Input}
                  onFocus={(event) => {
                    event.target.select();
                  }}
                  onValueChange={(values) => {
                    const nextQuantity = values.floatValue;
                    setQuantityValue(nextQuantity ?? '');
                    setValue('quantity', nextQuantity, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });

                    if (nextQuantity && nextQuantity > 0 && errors.quantity) {
                      clearErrors('quantity');
                    }
                  }}
                />
                <FormErrorMessage>{errors.quantity?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
          )}
        </Grid>

        <Grid
          templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
          columnGap={2}
          rowGap={2}
          marginTop={4}
          width="100%"
        >
          <FormControl isInvalid={!!errors.unit_measure_id}>
            {isSiengeBase ? (
              <SiengeFieldLabelWithTooltip
                label="Unidade de medida"
                tooltipLabel={siengeTooltipMessages.unitMeasure}
              />
            ) : (
              <FormLabel>Unidade de medida</FormLabel>
            )}

            <Controller
              render={(field) => (
                <OwnSelect
                  {...field}
                  data-testid="unit-measure-select"
                  isInvalid={!!errors.unit_measure_id}
                  placeholder="Selecione"
                  options={unitMeasuresToSelect}
                  value={unitMeasuresToSelect.filter(
                    (s) =>
                      String(s.value) === String(unitMeasureSelected || ''),
                  )}
                  isLoading={isFindingUnitMeasures}
                  isDisabled={
                    isFindingUnitMeasures || unitMeasuresToSelect.length === 0
                  }
                  onChange={(selected) => {
                    const rawValue = selected?.value;
                    const selectedValue =
                      rawValue === undefined || rawValue === null
                        ? ''
                        : String(rawValue);
                    setUnitMeasureSelected(selectedValue);

                    if (shouldUseIntegrationUnitMeasures) {
                      setValue(
                        'external_unit_measure_key',
                        selectedValue || undefined,
                      );
                      setValue('unit_measure_id', undefined);

                      if (!selectedValue) {
                        clearUnitMeasureSelection();
                      }
                    } else if (selectedValue) {
                      setValue('unit_measure_id', Number(selectedValue));
                      setValue('external_unit_measure_key', undefined);
                    } else {
                      clearUnitMeasureSelection();
                    }

                    if (selected && !!errors.unit_measure_id) {
                      clearErrors('unit_measure_id');
                    }
                  }}
                />
              )}
              name="unit_measure_id"
              control={control}
              rules={{ required: true }}
            />

            <FormErrorMessage>
              {errors.unit_measure_id && 'Unidade de medida é obrigatória.'}
            </FormErrorMessage>
          </FormControl>

          {isSiengeBase ? (
            <FormControl isInvalid={!!compositionGroupError}>
              {isSiengeBase ? (
                <SiengeFieldLabelWithTooltip
                  label="Grupo de serviço"
                  tooltipLabel={siengeTooltipMessages.serviceGroup}
                />
              ) : (
                <FormLabel>Grupo de Serviço</FormLabel>
              )}

              <InputGroup>
                <Input
                  type="text"
                  mb={{ base: 3, md: 0 }}
                  borderTopRightRadius="0"
                  borderBottomRightRadius="0"
                  placeholder="Selecione o Grupo de Serviço do Sienge Plataforma"
                  readOnly
                  isReadOnly
                  _readOnly={{
                    background: 'white',
                  }}
                  value={compositionGroupLabel}
                />

                <InputRightAddon px={0}>
                  <IconButton
                    height="100%"
                    colorScheme="green"
                    aria-label="Selecionar obra"
                    title="Selecionar obra"
                    borderRadius={!isCompositionGroupSelected ? 'base' : '0'}
                    borderTopLeftRadius="0"
                    borderBottomLeftRadius="0"
                    width="50px"
                    onClick={() => {
                      openModal('compositionGroupsLookup', {
                        mode: 'lookup',
                        data: {
                          resourceType: 'service',
                        },
                        onConfirm: ({ parentGroup, selectedGroup }) => {
                          setCompositionGroup({
                            parent: parentGroup,
                            selected: selectedGroup,
                          });
                          setValue(
                            'external_classification_key',
                            parentGroup?.key,
                            {
                              shouldValidate: true,
                            },
                          );
                          setValue('external_type_key', selectedGroup?.key, {
                            shouldValidate: true,
                          });
                          clearErrors([
                            'external_classification_key',
                            'external_type_key',
                          ]);
                        },
                      });
                    }}
                  >
                    <Icon as={TableSearchIcon} w="20px" h="20px" />
                  </IconButton>
                </InputRightAddon>

                {isCompositionGroupSelected && (
                  <InputRightAddon px={0}>
                    <IconButton
                      height="100%"
                      colorScheme="red"
                      aria-label="Remover obra orçamento"
                      title="Remover obra orçamento"
                      borderTopLeftRadius="0"
                      borderBottomLeftRadius="0"
                      width="50px"
                      onClick={() => {
                        setCompositionGroup({});
                        setValue('external_classification_key', undefined);
                        setValue('external_type_key', undefined);
                        clearErrors([
                          'external_classification_key',
                          'external_type_key',
                        ]);
                      }}
                    >
                      <Icon as={TableRemoveIcon} w="20px" h="20px" />
                    </IconButton>
                  </InputRightAddon>
                )}
              </InputGroup>

              <FormErrorMessage>
                {compositionGroupError && 'Grupo de Serviço é obrigatória.'}
              </FormErrorMessage>
            </FormControl>
          ) : (
            <>
              <FormControl isInvalid={!!errors.classification_id}>
                <FormLabel>Grupo de serviço</FormLabel>

                <Controller
                  render={(field) => (
                    <OwnSelect
                      {...field}
                      isInvalid={!!errors.classification_id}
                      placeholder={
                        isFindingClassifications ? 'Carregando' : 'Selecione'
                      }
                      options={classificationsToSelect}
                      value={classificationsToSelect.filter(
                        (s) => Number(s.value) === classificationSelected,
                      )}
                      isDisabled={
                        isFindingClassifications ||
                        classificationsToSelect.length === 0
                      }
                      isLoading={isFindingClassifications}
                      onChange={(selected) => {
                        setClassificationSelected(Number(selected?.value));
                        setValue('classification_id', Number(selected?.value));

                        if (selected && !!errors.classification_id) {
                          clearErrors('classification_id');
                        }
                      }}
                    />
                  )}
                  name="classification_id"
                  control={control}
                  rules={{ required: true }}
                />

                <FormErrorMessage>
                  {errors.classification_id && 'Classificação é obrigatória.'}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.type_id}>
                <FormLabel>Subgrupo de serviço</FormLabel>

                <Controller
                  render={(field) => (
                    <OwnSelect
                      {...field}
                      isInvalid={!!errors.type_id}
                      placeholder={
                        isFindingClassificationTypes
                          ? 'Carregando'
                          : 'Selecione'
                      }
                      options={classificationTypesToSelect}
                      value={classificationTypesToSelect.filter(
                        (s) => Number(s.value) === classificationTypeSelected,
                      )}
                      isDisabled={
                        isFindingClassificationTypes ||
                        classificationTypesToSelect.length === 0
                      }
                      isLoading={isFindingClassificationTypes}
                      onChange={(selected) => {
                        setClassificationTypeSelected(Number(selected?.value));
                        setValue('type_id', Number(selected?.value));

                        if (selected && !!errors.type_id) {
                          clearErrors('type_id');
                        }
                      }}
                    />
                  )}
                  name="type_id"
                  control={control}
                  rules={{ required: true }}
                />

                <FormErrorMessage>
                  {errors.type_id && 'Subgrupo de serviço é obrigatório.'}
                </FormErrorMessage>
              </FormControl>
            </>
          )}
        </Grid>

        <Grid
          templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
          columnGap={2}
          rowGap={2}
          marginTop={4}
          width="100%"
        >
          <FormControl marginTop="4" isInvalid={!!errors?.observation}>
            <FormLabel>Observação</FormLabel>
            <Textarea resize="none" {...register('observation')} />
            <FormErrorMessage>Observação é obrigatória</FormErrorMessage>
          </FormControl>
        </Grid>
      </FormContentWrapper>

      <Flex w="100%" display="flex" justifyContent="center" alignItems="center">
        <Button
          width={{ base: '100%', md: 'lg' }}
          mt="4"
          colorScheme="blue"
          isLoading={saving}
          disabled={saving}
          type="submit"
          rightIcon={<Icon as={ArrowRightIcon} />}
        >
          Avançar
        </Button>
      </Flex>
    </Flex>
  );
};

export default FormCompositionIdentification;
